import { useCallback } from 'react';
import type { SearchRequestFn, SearchRequestParams, SearchRequestResult } from '../types';
import { buildSearchUrl, parseResponseData } from '../helpers';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

export const useSearchRequest = <T>(
  searchEndpoint?: string,
  otherSearchEndpoint?: string
): SearchRequestFn<T> | null => {
  const { entity } = useAuth();
  const hasRemoteSource = Boolean(searchEndpoint || otherSearchEndpoint);

  const request = useCallback(
    async (params?: SearchRequestParams): Promise<SearchRequestResult<T>> => {
      const url = buildSearchUrl(entity?.slug, searchEndpoint, otherSearchEndpoint);

      if (!url) {
        return { items: [], total: 0 };
      }

      const requestParams: Record<string, unknown> = {};

      if (params?.page !== undefined) {
        requestParams.page = params.page + 1;
      }

      if (params?.pageSize !== undefined) {
        requestParams.page_size = params.pageSize;
      }

      if (params?.query) {
        requestParams.search = params.query;
      }

      const response = await api.get(url, { params: requestParams });

      if (response.status !== 200) {
        throw new Error(`Search request failed with status ${response.status}`);
      }

      return parseResponseData<T>(response.data);
    },
    [entity?.slug, otherSearchEndpoint, searchEndpoint]
  );

  return hasRemoteSource ? request : null;
};
